/**
 * PDFGenerator Styles
 * StyleSheet definitions for PDF document rendering
 * Design: Technical/Engineering with focus on readability
 */

import { StyleSheet } from '@react-pdf/renderer';

// Color Palette - Technical/Engineering
const colors = {
  // Primary
  primary: '#374151',      // Dark gray
  primaryDark: '#1f2937',  // Darker gray

  // Accent
  accent: '#0d9488',       // Teal
  accentLight: '#14b8a6',  // Light teal
  accentBg: '#f0fdfa',     // Very light teal background

  // Neutrals
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',

  // Borders
  border: '#e5e7eb',
  borderDark: '#d1d5db',
};

export const styles = StyleSheet.create({
  // Page layout
  page: {
    flexDirection: 'column',
    backgroundColor: colors.white,
    padding: '40px 35px 60px 35px',
    fontFamily: 'Helvetica',
  },

  // Typography
  title: {
    fontSize: 22,
    marginBottom: 8,
    textAlign: 'left',
    fontWeight: 'bold',
    color: colors.primaryDark,
    letterSpacing: 0.5,
  },
  titleUnderline: {
    width: 60,
    height: 3,
    backgroundColor: colors.accent,
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 13,
    marginBottom: 12,
    marginTop: 24,
    fontWeight: 'bold',
    color: colors.primary,
    backgroundColor: colors.gray100,
    padding: '10px 14px',
    borderLeft: 3,
    borderLeftColor: colors.accent,
    textAlign: 'left',
    letterSpacing: 0.3,
  },
  sessionInfo: {
    fontSize: 10,
    marginBottom: 4,
    color: colors.gray600,
    textAlign: 'left',
  },
  sessionInfoContainer: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: 1,
    borderBottomColor: colors.border,
  },

  // Layout
  section: {
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 20,
  },
  headerInfo: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },

  // Basic table styles - cleaner design
  table: {
    width: '100%',
    maxWidth: 480,
    marginBottom: 16,
    alignSelf: 'flex-start',
  },
  tableRow: {
    margin: 0,
    flexDirection: 'row',
    borderBottom: 1,
    borderBottomColor: colors.border,
  },
  tableRowLast: {
    borderBottom: 0,
  },
  tableColHeader: {
    width: '45%',
    padding: '10px 12px',
    backgroundColor: colors.gray100,
  },
  tableCol: {
    width: '55%',
    padding: '10px 12px',
    backgroundColor: colors.white,
  },
  tableCellHeader: {
    margin: 0,
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.gray700,
    textAlign: 'left',
  },
  tableCell: {
    margin: 0,
    fontSize: 10,
    textAlign: 'left',
    color: colors.gray800,
  },
  tableCellValue: {
    margin: 0,
    fontSize: 10,
    textAlign: 'left',
    color: colors.accent,
    fontWeight: 'bold',
  },

  // CSV table styles (multi-column) - improved readability
  csvTable: {
    width: '100%',
    marginBottom: 16,
    alignSelf: 'flex-start',
    borderTop: 2,
    borderTopColor: colors.accent,
  },
  csvTableColHeader: {
    flex: 1,
    minWidth: 100,
    padding: '6px 5px',
    backgroundColor: colors.primary,
    borderRight: 1,
    borderRightColor: colors.primaryDark,
  },
  csvTableColHeaderLast: {
    borderRight: 0,
  },
  csvTableCol: {
    flex: 1,
    minWidth: 100,
    padding: '5px 5px',
    backgroundColor: colors.white,
    borderRight: 1,
    borderRightColor: colors.border,
    borderBottom: 1,
    borderBottomColor: colors.border,
  },
  csvTableColLast: {
    borderRight: 0,
  },
  csvTableColHeaderFirst: {
    width: 180,
    minWidth: 180,
    flexShrink: 0,
    flexGrow: 0,
    padding: '8px 10px',
    backgroundColor: colors.primaryDark,
    borderRight: 1,
    borderRightColor: colors.primary,
  },
  csvTableColFirst: {
    width: 180,
    minWidth: 180,
    flexShrink: 0,
    flexGrow: 0,
    padding: '7px 10px',
    backgroundColor: colors.gray50,
    borderRight: 1,
    borderRightColor: colors.border,
    borderBottom: 1,
    borderBottomColor: colors.border,
  },
  csvTableCellHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'left',
    color: colors.white,
    letterSpacing: 0.2,
  },
  csvTableCell: {
    fontSize: 8,
    textAlign: 'left',
    color: colors.gray800,
  },
  csvTableCellLabel: {
    fontSize: 8,
    textAlign: 'left',
    color: colors.gray700,
    fontWeight: 'bold',
  },

  // Row alternation styles - subtle contrast
  tableRowEven: {
    backgroundColor: colors.white,
  },
  tableRowOdd: {
    backgroundColor: colors.gray50,
  },

  // Highlight styles
  highlightCell: {
    backgroundColor: colors.accentBg,
    fontWeight: 'bold',
  },

  // Status indicators
  statusActive: {
    color: colors.accent,
    fontWeight: 'bold',
  },
  statusInactive: {
    color: colors.gray300,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 35,
    right: 35,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: colors.gray600,
  },
  pageNumber: {
    fontSize: 8,
    color: colors.gray600,
  },
});
